export class ErrorUtilities {

    /**
     * Determines whether the provided error represents an aborted operation.
     *
     * Recognizes browser-specific abort patterns:
     * - In Chrome/Edge, an abort typically surfaces as a `DOMException` with `name === 'AbortError'`.
     * - In Firefox/Safari, aborts frequently manifest as a `TypeError` whose message contains "abort".
     *
     * @param err - The error to inspect, which may be any unknown value.
     * @returns `true` if the error indicates an abort condition; otherwise `false`.
     */
    static isAbortError(err: unknown): boolean {
      // Chrome/Edge: DOMException with name 'AbortError'
      // Firefox/Safari often throw TypeError on abort
      return (
        (typeof DOMException !== 'undefined' && err instanceof DOMException && err.name === 'AbortError') ||
        (err instanceof TypeError && /abort/i.test(err.message))
      );
    }

    /**
     * Determines whether a given error is likely related to a CORS (Cross-Origin Resource Sharing) issue.
     *
     * This heuristic checks if the error is a `TypeError` and its message contains common CORS-related
     * substrings such as "Failed to fetch", "CORS", or "Access-Control-Allow-Origin". It is useful for
     * distinguishing network/CORS failures from other error types when handling fetch or HTTP requests
     * in the browser.
     *
     * @param err - The error to inspect, typically caught from a failed network request.
     * @returns `true` if the error appears to be CORS-related; otherwise, `false`.
     */
    static isLikelyCors(err: unknown): boolean {
      return (
        err instanceof TypeError &&
        typeof err.message === 'string' &&
        /(Failed to fetch|CORS|Access-Control-Allow-Origin)/i.test(err.message)
      );
    }
}
